import { useEffect, useState } from 'react';
import {
  CalendarCheck,
  Loader2,
  AlertCircle,
  Building,
  Building2,
  CalendarX,
  Search,
} from 'lucide-react';
import { BookingStatus, BookingSource } from '@pms/shared';
import { useBookingsOverview, usePlatformBookings } from '@/hooks/api/usePlatform';
import type { NamedCount, PlatformBooking } from '@/services/api/platform.api';
import { StatCard } from '@/components/ui/StatCard';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { StatusBadge } from '@/components/ui/StatusBadge';

const STATUS_LABELS: Record<BookingStatus, string> = {
  [BookingStatus.PENDING]: 'Pending',
  [BookingStatus.CONFIRMED]: 'Confirmed',
  [BookingStatus.CHECKED_IN]: 'Checked In',
  [BookingStatus.CHECKED_OUT]: 'Checked Out',
  [BookingStatus.CANCELLED]: 'Cancelled',
  [BookingStatus.NO_SHOW]: 'No Show',
};

const STATUS_BAR: Record<BookingStatus, string> = {
  [BookingStatus.PENDING]: 'bg-amber-400',
  [BookingStatus.CONFIRMED]: 'bg-brand-500',
  [BookingStatus.CHECKED_IN]: 'bg-green-500',
  [BookingStatus.CHECKED_OUT]: 'bg-gray-400',
  [BookingStatus.CANCELLED]: 'bg-red-400',
  [BookingStatus.NO_SHOW]: 'bg-orange-400',
};

const SOURCE_LABELS: Record<BookingSource, string> = {
  [BookingSource.DIRECT]: 'Direct',
  [BookingSource.AIRBNB]: 'Airbnb',
  [BookingSource.BOOKING_COM]: 'Booking.com',
  [BookingSource.EXPEDIA]: 'Expedia',
  [BookingSource.AXIS_ROOMS]: 'AxisRooms',
  [BookingSource.MANUAL]: 'Manual',
};

const PAGE_SIZE = 25;

function formatMoney(total: string, currency: string): string {
  const n = Number(total);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${currency} ${total}`;
  }
}

/** yyyy-mm-dd (pin to local midnight so the calendar day never drifts by TZ). */
function formatDay(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Full ISO timestamp → short date. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "2 adults · 1 child" from the party split, falling back to the guests count. */
function formatParty(b: Pick<PlatformBooking, 'adults' | 'children' | 'guests'>): string {
  if (b.adults != null) {
    const parts = [`${b.adults} adult${b.adults === 1 ? '' : 's'}`];
    if (b.children != null && b.children > 0) {
      parts.push(`${b.children} child${b.children === 1 ? '' : 'ren'}`);
    }
    return parts.join(' · ');
  }
  return `${b.guests} guest${b.guests === 1 ? '' : 's'}`;
}

/** Horizontal breakdown bars for a keyed set of counts. */
function BreakdownBars({
  rows,
}: {
  rows: { key: string; label: string; count: number; barClass: string }[];
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ul className="space-y-3">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-gray-700">{r.label}</span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums">{r.count}</span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden" aria-hidden="true">
            <div
              className={`h-full rounded-full ${r.barClass}`}
              style={{ width: `${Math.round((r.count / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function RankedList({
  rows,
  icon: Icon,
  emptyLabel,
}: {
  rows: NamedCount[];
  icon: typeof Building;
  emptyLabel: string;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-400">{emptyLabel}</p>;
  }
  return (
    <ul className="divide-y divide-gray-100">
      {rows.map((r) => (
        <li key={r.key} className="flex items-center gap-3 py-2.5">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand-50 shrink-0" aria-hidden="true">
            <Icon size={15} className="text-brand-600" />
          </div>
          <span className="flex-1 text-sm font-medium text-gray-800 truncate">{r.key}</span>
          <span className="text-sm font-semibold text-gray-900 tabular-nums">{r.count}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The individual cross-tenant bookings list (real guest bookings), below the
 * aggregate cards. Status filter + free-text search + Prev/Next pagination, each
 * with its own loading / empty / error state. Guest name + email are shown — this
 * is the platform operator's own console.
 */
function BookingsTable() {
  const [status, setStatus] = useState<BookingStatus | ''>('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);

  // Debounce the search box into the query param + reset to the first page.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, isError, isFetching, refetch } = usePlatformBookings({
    limit: PAGE_SIZE,
    offset,
    ...(status ? { status } : {}),
    ...(q ? { q } : {}),
  });

  const total = data?.total ?? 0;
  const rows = data?.rows ?? [];
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <section aria-labelledby="bookings-list-heading" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 id="bookings-list-heading" className="text-base font-semibold text-gray-900">
          Bookings
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search guest, email, reference, property…"
              aria-label="Search bookings"
              className="w-72 max-w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <label className="sr-only" htmlFor="booking-status-filter">
            Filter by status
          </label>
          <select
            id="booking-status-filter"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as BookingStatus | '');
              setOffset(0);
            }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="">All statuses</option>
            {Object.values(BookingStatus).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-brand-500" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading bookings…</span>
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <AlertCircle size={24} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
            <p className="text-sm text-red-700 mb-4">Unable to load bookings.</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={CalendarX}
            title="No bookings yet"
            description={
              q || status
                ? 'No bookings match your filters. Try clearing the search or status.'
                : 'Bookings made by guests across the marketplace will appear here.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <caption className="sr-only">
                Cross-tenant bookings across the BestAcco marketplace
              </caption>
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th scope="col" className="px-4 py-3">Reference</th>
                  <th scope="col" className="px-4 py-3">Guest</th>
                  <th scope="col" className="px-4 py-3">Email</th>
                  <th scope="col" className="px-4 py-3">Property</th>
                  <th scope="col" className="px-4 py-3">Dates</th>
                  <th scope="col" className="px-4 py-3">Guests</th>
                  <th scope="col" className="px-4 py-3 text-right">Total</th>
                  <th scope="col" className="px-4 py-3">Status</th>
                  <th scope="col" className="px-4 py-3">Booked</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                      {b.reference ?? <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-800 whitespace-nowrap">{b.guestName}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {b.guestEmail ?? <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-800">
                      <div className="font-medium">{b.propertyName}</div>
                      <div className="text-xs text-gray-400">{b.operatorName} · {b.unitName}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      <div>{formatDay(b.checkIn)} → {formatDay(b.checkOut)}</div>
                      <div className="text-xs text-gray-400">
                        {b.nights} night{b.nights === 1 ? '' : 's'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatParty(b)}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap tabular-nums">
                      {formatMoney(b.total, b.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={b.status}
                        category="booking"
                        label={STATUS_LABELS[b.status]}
                      />
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {formatDate(b.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination — only when there is more than one page */}
      {!isLoading && !isError && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-500" aria-live="polite">
            Showing <span className="font-medium text-gray-700">{from}</span>–
            <span className="font-medium text-gray-700">{to}</span> of{' '}
            <span className="font-medium text-gray-700">{total}</span>
            {isFetching && <span className="ml-2 text-gray-400">Updating…</span>}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              disabled={!canPrev}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              disabled={!canNext}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function AdminBookingsPage() {
  const { data, isLoading, isError, refetch } = useBookingsOverview();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={28} className="animate-spin text-brand-500" aria-hidden="true" />
        <span className="ml-2 text-sm text-gray-500">Loading bookings…</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
        <AlertCircle size={28} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
        <p className="text-sm text-red-700 mb-4">Unable to load bookings overview.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const hasBookings = data.totalBookings > 0;

  const statusRows = Object.values(BookingStatus).map((s) => ({
    key: s,
    label: STATUS_LABELS[s],
    count: data.byStatus[s] ?? 0,
    barClass: STATUS_BAR[s],
  }));

  const sourceRows = Object.values(BookingSource).map((s) => ({
    key: s,
    label: SOURCE_LABELS[s],
    count: data.bySource[s] ?? 0,
    barClass: 'bg-brand-500',
  }));

  return (
    <div className="space-y-8">
      <PageHeader
        title="Bookings"
        description="Cross-tenant bookings across the BestAcco marketplace — aggregate insights plus every individual booking (guest, dates, and status)."
      />

      {hasBookings ? (
        <>
          {/* Headline stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={CalendarCheck}
              value={data.totalBookings}
              label="Total bookings"
              borderColor="border-l-brand-500"
            />
            <StatCard
              icon={Building}
              value={data.topOperators.length}
              label="Operators with bookings"
              borderColor="border-l-purple-500"
            />
            <StatCard
              icon={Building2}
              value={data.topProperties.length}
              label="Properties with bookings"
              borderColor="border-l-green-500"
            />
            <StatCard
              icon={CalendarCheck}
              value={data.revenueByCurrency.length}
              label="Currencies"
              borderColor="border-l-amber-500"
            />
          </div>

          {/* Revenue by currency */}
          <section aria-labelledby="revenue-heading">
            <h2 id="revenue-heading" className="text-base font-semibold text-gray-900 mb-3">
              Revenue by currency
            </h2>
            {data.revenueByCurrency.length === 0 ? (
              <p className="text-sm text-gray-400">No revenue recorded.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.revenueByCurrency.map((r) => (
                  <div
                    key={r.currency}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
                  >
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      {r.currency}
                    </p>
                    <p className="mt-1 text-2xl font-bold text-gray-900 leading-tight">
                      {formatMoney(r.total, r.currency)}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {r.count} booking{r.count === 1 ? '' : 's'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Status + source breakdowns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section
              aria-labelledby="status-heading"
              className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
            >
              <h2 id="status-heading" className="text-base font-semibold text-gray-900 mb-4">
                Bookings by status
              </h2>
              <BreakdownBars rows={statusRows} />
            </section>

            <section
              aria-labelledby="source-heading"
              className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
            >
              <h2 id="source-heading" className="text-base font-semibold text-gray-900 mb-4">
                Bookings by source
              </h2>
              <BreakdownBars rows={sourceRows} />
            </section>
          </div>

          {/* Top operators + properties */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section
              aria-labelledby="operators-heading"
              className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
            >
              <h2 id="operators-heading" className="text-base font-semibold text-gray-900 mb-3">
                Top operators
              </h2>
              <RankedList rows={data.topOperators} icon={Building} emptyLabel="No operators with bookings yet." />
            </section>

            <section
              aria-labelledby="properties-heading"
              className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
            >
              <h2 id="properties-heading" className="text-base font-semibold text-gray-900 mb-3">
                Top properties
              </h2>
              <RankedList rows={data.topProperties} icon={Building2} emptyLabel="No properties with bookings yet." />
            </section>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <EmptyState
            icon={CalendarX}
            title="No bookings yet"
            description="Aggregates and individual bookings will appear here once operators start taking bookings."
          />
        </div>
      )}

      {/* Individual bookings list (always available for the super-admin) */}
      <BookingsTable />
    </div>
  );
}
